/**
 * @file image-ops-web.ts
 * @description Pure-JS fallbacks for image operations.
 * Used by ImageOps when not running on Tauri or when below the pixel threshold.
 * NEVER import this module directly from coordinators — always go through image-ops.ts.
 */

export type FilterId =
    | 'grayscale' | 'sepia' | 'invert'
    | 'sharpen'   | 'emboss' | 'edge_detect';

export interface ColorAdjustParams {
    brightness?: number; // -100..+100
    contrast?:   number; // -100..+100
    saturation?: number; // -100..+100
    hue?:        number; // -180..+180 degrees
    exposure?:   number; // -3..+3 EV
}

// ─── Kernel convolution ────────────────────────────────────────────────────────

function applyKernel3x3(
    src: Uint8ClampedArray,
    w: number, h: number,
    kernel: number[]
): Uint8ClampedArray {
    const dst = new Uint8ClampedArray(src.length);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const idx = (y * w + x) * 4;
            if (x === 0 || x === w - 1 || y === 0 || y === h - 1) {
                dst[idx]     = src[idx];
                dst[idx + 1] = src[idx + 1];
                dst[idx + 2] = src[idx + 2];
                dst[idx + 3] = src[idx + 3];
                continue;
            }
            let r = 0, g = 0, b = 0;
            for (let ky = 0; ky < 3; ky++) {
                for (let kx = 0; kx < 3; kx++) {
                    const nx = x + kx - 1;
                    const ny = y + ky - 1;
                    const ni = (ny * w + nx) * 4;
                    const k = kernel[ky * 3 + kx];
                    r += src[ni]     * k;
                    g += src[ni + 1] * k;
                    b += src[ni + 2] * k;
                }
            }
            dst[idx]     = Math.max(0, Math.min(255, r));
            dst[idx + 1] = Math.max(0, Math.min(255, g));
            dst[idx + 2] = Math.max(0, Math.min(255, b));
            dst[idx + 3] = src[idx + 3];
        }
    }
    return dst;
}

// ─── Gaussian blur (separable box-blur approximation) ─────────────────────────

function gaussianKernel1d(radius: number): number[] {
    const sigma = Math.max(radius / 3, 0.5);
    const half = Math.ceil(radius);
    const size = half * 2 + 1;
    const weights: number[] = [];
    let sum = 0;
    for (let i = 0; i < size; i++) {
        const x = i - half;
        const w = Math.exp(-(x * x) / (2 * sigma * sigma));
        weights.push(w);
        sum += w;
    }
    return weights.map(w => w / sum);
}

function blurH(src: Uint8ClampedArray, w: number, h: number, weights: number[]): Uint8ClampedArray {
    const half = (weights.length - 1) / 2;
    const dst = new Uint8ClampedArray(src.length);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let wi = 0; wi < weights.length; wi++) {
                const sx = Math.max(0, Math.min(w - 1, x + wi - half));
                const si = (y * w + sx) * 4;
                r += src[si]     * weights[wi];
                g += src[si + 1] * weights[wi];
                b += src[si + 2] * weights[wi];
                a += src[si + 3] * weights[wi];
            }
            const di = (y * w + x) * 4;
            dst[di]     = r;
            dst[di + 1] = g;
            dst[di + 2] = b;
            dst[di + 3] = a;
        }
    }
    return dst;
}

function blurV(src: Uint8ClampedArray, w: number, h: number, weights: number[]): Uint8ClampedArray {
    const half = (weights.length - 1) / 2;
    const dst = new Uint8ClampedArray(src.length);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let wi = 0; wi < weights.length; wi++) {
                const sy = Math.max(0, Math.min(h - 1, y + wi - half));
                const si = (sy * w + x) * 4;
                r += src[si]     * weights[wi];
                g += src[si + 1] * weights[wi];
                b += src[si + 2] * weights[wi];
                a += src[si + 3] * weights[wi];
            }
            const di = (y * w + x) * 4;
            dst[di]     = r;
            dst[di + 1] = g;
            dst[di + 2] = b;
            dst[di + 3] = a;
        }
    }
    return dst;
}

// ─── HSL conversion ───────────────────────────────────────────────────────────

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    const l = (max + min) / 2;

    if (delta < 1e-6) return [0, 0, l];

    const s = delta / Math.max(1e-6, 1 - Math.abs(2 * l - 1));
    let h: number;
    if (max === r)      h = ((g - b) / delta % 6) / 6;
    else if (max === g) h = ((b - r) / delta + 2) / 6;
    else                h = ((r - g) / delta + 4) / 6;

    return [(h + 1) % 1, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if      (h < 1/6) { r = c; g = x; }
    else if (h < 2/6) { r = x; g = c; }
    else if (h < 3/6) { g = c; b = x; }
    else if (h < 4/6) { g = x; b = c; }
    else if (h < 5/6) { r = x; b = c; }
    else              { r = c; b = x; }
    return [r + m, g + m, b + m];
}

// ─── Public API ───────────────────────────────────────────────────────────────

export class ImageOpsWeb {
    static applyFilter(
        data: Uint8ClampedArray, w: number, h: number,
        filterId: FilterId, _intensity = 1.0
    ): Uint8ClampedArray {
        switch (filterId) {
            case 'grayscale': {
                const dst = new Uint8ClampedArray(data);
                for (let i = 0; i < dst.length; i += 4) {
                    const lum = (dst[i] * 0.299 + dst[i+1] * 0.587 + dst[i+2] * 0.114) | 0;
                    dst[i] = dst[i+1] = dst[i+2] = lum;
                }
                return dst;
            }
            case 'sepia': {
                const dst = new Uint8ClampedArray(data);
                for (let i = 0; i < dst.length; i += 4) {
                    const r = data[i], g = data[i+1], b = data[i+2];
                    dst[i]   = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189);
                    dst[i+1] = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168);
                    dst[i+2] = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131);
                }
                return dst;
            }
            case 'invert': {
                const dst = new Uint8ClampedArray(data);
                for (let i = 0; i < dst.length; i += 4) {
                    dst[i] = 255 - dst[i];
                    dst[i+1] = 255 - dst[i+1];
                    dst[i+2] = 255 - dst[i+2];
                }
                return dst;
            }
            case 'sharpen':
                return applyKernel3x3(data, w, h, [0, -1, 0, -1, 5, -1, 0, -1, 0]);
            case 'emboss':
                return applyKernel3x3(data, w, h, [-2, -1, 0, -1, 1, 1, 0, 1, 2]);
            case 'edge_detect':
                return applyKernel3x3(data, w, h, [-1, -1, -1, -1, 8, -1, -1, -1, -1]);
            default:
                return new Uint8ClampedArray(data);
        }
    }

    static gaussianBlur(
        data: Uint8ClampedArray, w: number, h: number, radius: number
    ): Uint8ClampedArray {
        const k = gaussianKernel1d(Math.max(radius, 0.5));
        return blurV(blurH(data, w, h, k), w, h, k);
    }

    static colorAdjust(
        data: Uint8ClampedArray, w: number, h: number,
        params: ColorAdjustParams
    ): Uint8ClampedArray {
        const bri = (params.brightness ?? 0) / 100 * 0.5;
        const conFactor = (params.contrast ?? 0) >= 0
            ? 1 + (params.contrast ?? 0) / 100 * 2
            : 1 + (params.contrast ?? 0) / 100;
        const satFactor = 1 + (params.saturation ?? 0) / 100;
        const hueShift = (params.hue ?? 0) / 360;
        const expFactor = Math.pow(2, params.exposure ?? 0);

        const dst = new Uint8ClampedArray(data.length);
        for (let i = 0; i < data.length; i += 4) {
            const r = Math.min(1, data[i]   / 255 * expFactor);
            const g = Math.min(1, data[i+1] / 255 * expFactor);
            const b = Math.min(1, data[i+2] / 255 * expFactor);

            let [h, s, l] = rgbToHsl(r, g, b);
            h = ((h + hueShift) % 1 + 1) % 1;
            s = Math.max(0, Math.min(1, s * satFactor));
            l = Math.max(0, Math.min(1, l + bri));
            l = Math.max(0, Math.min(1, (l - 0.5) * conFactor + 0.5));

            const [nr, ng, nb] = hslToRgb(h, s, l);
            dst[i]   = nr * 255;
            dst[i+1] = ng * 255;
            dst[i+2] = nb * 255;
            dst[i+3] = data[i+3];
        }
        return dst;
    }

    static floodFill(
        data: Uint8ClampedArray, w: number, h: number,
        x: number, y: number,
        fillColor: [number, number, number, number],
        tolerance: number
    ): Uint8ClampedArray {
        const dst = new Uint8ClampedArray(data);
        const sx = Math.floor(x);
        const sy = Math.floor(y);
        if (sx < 0 || sx >= w || sy < 0 || sy >= h) return dst;

        const si = (sy * w + sx) * 4;
        const [sr, sg, sb, sa] = [dst[si], dst[si+1], dst[si+2], dst[si+3]];
        const [fr, fg, fb, fa] = fillColor;
        const tol = tolerance;

        if (Math.abs(sr-fr) <= 1 && Math.abs(sg-fg) <= 1 && Math.abs(sb-fb) <= 1 && Math.abs(sa-fa) <= 1) {
            return dst;
        }

        const matches = (idx: number) =>
            Math.abs(dst[idx]   - sr) <= tol &&
            Math.abs(dst[idx+1] - sg) <= tol &&
            Math.abs(dst[idx+2] - sb) <= tol &&
            Math.abs(dst[idx+3] - sa) <= tol;

        const visited = new Uint8Array(w * h);
        const stack: number[] = [sy * w + sx];
        visited[sy * w + sx] = 1;

        while (stack.length > 0) {
            const pi = stack.pop()!;
            const idx = pi * 4;
            dst[idx]   = fr;
            dst[idx+1] = fg;
            dst[idx+2] = fb;
            dst[idx+3] = fa;

            const cy = (pi / w) | 0;
            const cx = pi % w;
            const neighbors: [boolean, number][] = [
                [cy > 0,       pi - w],
                [cy < h - 1,   pi + w],
                [cx > 0,       pi - 1],
                [cx < w - 1,   pi + 1],
            ];
            for (const [valid, ni] of neighbors) {
                if (valid && !visited[ni] && matches(ni * 4)) {
                    visited[ni] = 1;
                    stack.push(ni);
                }
            }
        }
        return dst;
    }

    static encodeImage(
        data: Uint8ClampedArray, w: number, h: number,
        format: 'png' | 'jpeg' | 'webp',
        quality = 92
    ): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d')!;
            ctx.putImageData(new ImageData(data, w, h), 0, 0);
            const mime = format === 'jpeg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : 'image/png';
            canvas.toBlob(
                (blob) => blob ? blob.arrayBuffer().then(resolve).catch(reject) : reject(new Error('toBlob failed')),
                mime,
                quality / 100
            );
        });
    }
}
